// The desk's own page (/desk): a full-screen conversation, like a chat app. Saved conversations on
// the right; in the middle the conversation, its cards (desk-cards: picks, the market, results, charts)
// and the message box, with suggestion cards written from the market now just above it. Here a chart
// the desk opens is drawn in the conversation (the stock page's daily chart and engine) instead of
// leaving for the stock page, and a paper plan is marked on it.
import { useEffect, useRef, useState } from 'react';
import { ActivityIcon, BarChart3Icon, CrosshairIcon, NotebookPenIcon, PlusIcon, TrendingDownIcon, TrendingUpIcon, type LucideIcon } from 'lucide-react';
import { PATTERN_RECORD } from '@planb/core';
import { useFeatures, useMarket } from '@/lib/client/use-market';
import { Navigate } from 'react-router';
import { displayName, useMe } from '@/lib/client/use-me';
import { usePageTools } from '@/lib/desk/tools';
import { useSuggestions, type Suggestion } from '@/lib/desk/suggest';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { AppHeader } from './app-header';
import { setupStatus, type ChartHistory } from './chart-history';
import { carry, dateSeed } from './common';
import { DeskComposer, DeskMessages, takeQueued, ThreadList, useDeskChat } from './desk';
import type { CardActions } from './desk-cards';
import { DeskIcon } from './market-ui';

const ICON: Record<Suggestion['kind'], LucideIcon> = { market: ActivityIcon, mover: TrendingUpIcon, faller: TrendingDownIcon, sector: BarChart3Icon, setups: CrosshairIcon, paper: NotebookPenIcon, start: DeskIcon as unknown as LucideIcon };

function SuggestionCards({ items, onPick, compact }: { items: Suggestion[] | null; onPick: (q: string) => void; compact?: boolean }) {
  if (items === null) {
    return <div className={cn('grid gap-2', compact ? 'grid-flow-col auto-cols-[minmax(14rem,1fr)]' : 'sm:grid-cols-2')}>{[0, 1, 2, 3].slice(0, compact ? 3 : 4).map((i) => <div key={i} className="h-[74px] animate-pulse rounded-xl border bg-muted/40" />)}</div>;
  }
  if (!items.length) return null;
  return (
    <div className={cn(compact ? 'flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]' : 'grid gap-2 sm:grid-cols-2')} aria-label="Suggestions from the market now">
      {items.map((s) => {
        const Icon = ICON[s.kind];
        return (
          <button key={s.id} type="button" onClick={() => onPick(s.prompt)}
            className={cn('group flex min-w-0 items-start gap-2.5 rounded-xl border bg-card p-3 text-left transition-colors hover:border-brand-strong/60 hover:bg-brand-soft/40', compact && 'w-64 shrink-0 p-2.5')}>
            <span className={cn('grid size-7 shrink-0 place-items-center rounded-lg bg-tint', s.kind === 'faller' ? 'text-bear' : s.kind === 'mover' ? 'text-bull' : 'text-brand-strong')}><Icon className="size-3.5" /></span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-medium text-ink group-hover:text-brand-strong">{s.title}</span>
              <span className={cn('block text-[11.5px] text-muted-foreground', compact ? 'truncate' : 'line-clamp-2')}>{s.detail}</span>
              {s.asOf && !compact && <span className="mt-0.5 block text-[10.5px] text-muted-foreground/80">{s.asOf}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Shown while its release switch is on for the user (and they have the desk: approved members). */
export function DeskPage() {
  const feat = useFeatures();
  const me = useMe();
  if (feat.ready && !feat.on('desk_page')) return <Navigate to="/" replace />;
  if (me && !me.access) return <Navigate to="/" replace />;
  return <DeskScreen />;
}

function DeskScreen() {
  const [seed] = useState(dateSeed);
  const { session, status, mode, live, user, role } = useMarket({ index: 'NIFTY500', seed });
  const query = carry({ index: 'NIFTY500', live, seed });
  const me = useMe();
  const chat = useDeskChat();
  const { msgs, send, busy } = chat;
  const suggestions = useSuggestions(session, mode);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Open the last conversation (or the question handed over), and the conversation list.
  useEffect(() => {
    void chat.loadThreads();
    const q = takeQueued();
    const params = new URLSearchParams(location.search);
    const thread = params.get('thread');
    if (q) void send(q);
    else if (thread) void chat.loadThread(thread);
    else if (params.get('new')) chat.newChat();
    else chat.restore();
    if (thread || params.get('new')) history.replaceState(null, '', '/desk');
    inputRef.current?.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { document.title = 'Desk · MoneyHQ'; }, []);
  useEffect(() => { if (msgs.length) endRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' }); }, [msgs]);

  // A chart the desk opens is drawn here, in the conversation; read_page reads it with the stock
  // page's daily engine (the breakout rule, momentum, chart patterns), and a plan is marked on it.
  const lastChart = useRef<{ symbol: string; h: ChartHistory | null } | null>(null);
  const chartReady = useRef<((h: ChartHistory) => void) | null>(null);
  const chartCard = (symbol: string) => {
    lastChart.current = { symbol, h: null };
    chat.addCard({ kind: 'chart', tool: 'show_chart', data: { symbol } });
  };
  const readChart = () => {
    const c = lastChart.current;
    if (!c?.h || !c.h.daily.length) return { page: 'desk', note: 'No chart is drawn in the conversation yet. Use show_chart with a symbol to draw its daily chart here.' };
    const h = c.h, last = h.daily[h.daily.length - 1];
    const st = setupStatus(h);
    const forming = h.patterns.filter((p) => p.breakIdx === null).slice(-2);
    return {
      page: 'desk', symbol: c.symbol, chart: {
        timeframe: '1D', note: 'The daily chart drawn in the conversation (stored NSE candles, adjusted for splits and bonuses). Intraday levels, VWAP and today\'s order flow are on the stock page.',
        lastClose: last.c, lastSession: new Date(last.t * 1000).toISOString().slice(0, 10),
        newHighBreakout: st.breakout, momentum: st.momentum,
        chartPatterns: { forming: forming.map((p) => ({ name: p.name, level: +p.level.to.p.toFixed(2), tested: PATTERN_RECORD[p.name] })) },
      },
    };
  };
  usePageTools({
    show_chart: async (a) => {
      const sym = String(a.symbol ?? lastChart.current?.symbol ?? '').trim().toUpperCase();
      if (!/^[A-Z0-9&-]{1,20}$/.test(sym)) return { error: 'Give an NSE symbol to chart' };
      const ready = new Promise<ChartHistory>((resolve) => { chartReady.current = resolve; });
      chartCard(sym);
      const h = await Promise.race([ready, new Promise<null>((r) => setTimeout(() => r(null), 15000))]);
      if (!h) return { error: `${sym}'s chart didn't load in time` };
      lastChart.current = { symbol: sym, h };
      return { ...readChart(), shown: 'the daily chart, drawn in the conversation' };
    },
    read_page: () => readChart(),
    mark_chart: (a) => {
      const levels = (Array.isArray(a.levels) ? a.levels : []) as { price: number; label: string; kind: string }[];
      if (!lastChart.current) return { error: 'No chart is drawn in the conversation' };
      setMarks(levels.map((l) => ({ price: Number(l.price), label: String(l.label ?? ''), kind: String(l.kind ?? 'entry') })));
      return { ok: true, marked: levels.length };
    },
  });
  const [marks, setMarks] = useState<{ price: number; label: string; kind: string }[]>([]);
  const act: CardActions = { ask: (q) => void send(q), chart: (sym) => chartCard(sym) };
  const pick = (q: string) => { void send(q); };

  const empty = msgs.length === 0;
  const name = displayName(me).split(' ')[0];
  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="p-3">
        <Button variant="outline" className="w-full justify-start gap-2 rounded-xl" onClick={() => { chat.newChat(); inputRef.current?.focus(); }}><PlusIcon className="size-4" />New chat</Button>
      </div>
      <p className="px-4 pb-1 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">Conversations</p>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
        <ThreadList chat={chat} dense onOpen={(id) => { void chat.loadThread(id); }} />
      </div>
    </div>
  );

  return (
    <>
      <AppHeader section="desk" mode={mode} status={status} user={user} role={role} query={query} />
      <div className="lg:pl-(--sidebar-w)">
        <div className="flex min-h-[calc(100dvh-3.5rem)]">
          <main className="relative flex min-w-0 flex-1 flex-col">
            {/* Phones: the conversations are on the menu page (More); New starts one here. */}
            {!empty && (
              <div className="flex items-center justify-end px-4 pt-3 md:hidden">
                <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" onClick={() => chat.newChat()}><PlusIcon className="size-4" />New chat</Button>
              </div>
            )}
            {empty ? (
              <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col justify-center px-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] lg:pb-16">
                <div className="mb-6 text-center">
                  <h1 className="font-heading text-[28px] font-semibold tracking-tight text-ink sm:text-[32px]">{name ? `Where should we start, ${name}?` : 'Where should we start?'}</h1>
                  <p className="mt-1.5 text-[13.5px] text-muted-foreground">Ask about a stock, the market, or a trade. Every level and record comes from MoneyHQ&apos;s engine and tests.</p>
                </div>
                <DeskComposer chat={chat} inputRef={inputRef} big placeholder="Ask MoneyHQ…"
                  above={<div className="mb-3"><SuggestionCards items={suggestions} onPick={pick} /></div>} />
                <p className="mt-2 text-center text-[11px] text-muted-foreground">Technical analysis for paper trading, not investment advice.</p>
              </div>
            ) : (
              <>
                <div className="mx-auto w-full max-w-3xl flex-1 space-y-5 px-4 pt-6 pb-6 text-[14px] leading-relaxed">
                  <DeskMessagesWithMarks chat={chat} act={act} marks={marks} onChart={(sym, h) => { if (lastChart.current?.symbol !== sym) return; lastChart.current.h = h; chartReady.current?.(h); chartReady.current = null; }} />
                  <div ref={endRef} />
                </div>
                <div className="sticky bottom-0 z-10 bg-gradient-to-t from-background via-background to-background/0 px-4 pt-4 pb-[calc(4.5rem+env(safe-area-inset-bottom))] lg:pb-4">
                  <div className="mx-auto w-full max-w-3xl">
                    <DeskComposer chat={chat} inputRef={inputRef} big placeholder="Ask a follow-up…"
                      above={!busy && <div className="mb-2 max-sm:hidden"><SuggestionCards items={suggestions} onPick={pick} compact /></div>} />
                    <p className="mt-1.5 text-center text-[10.5px] text-muted-foreground max-lg:hidden">Technical analysis for paper trading, not investment advice.</p>
                  </div>
                </div>
              </>
            )}
          </main>
          {/* Laptops and tablets: the conversations on the right of the chat (phones: the menu page). */}
          <aside className="sticky top-14 hidden h-[calc(100dvh-3.5rem)] w-64 shrink-0 border-l bg-card/40 md:block">{sidebar}</aside>
        </div>
      </div>
    </>
  );
}

/** The messages, with this page's chart cards wired to the chart tools (loaded data, plan marks). */
function DeskMessagesWithMarks({ chat, act, marks, onChart }: { chat: ReturnType<typeof useDeskChat>; act: CardActions; marks: { price: number; label: string; kind: string }[]; onChart: (symbol: string, h: ChartHistory) => void }) {
  return <DeskMessages chat={chat} act={act} wide chartProps={{ marks, onReady: onChart }} />;
}
